import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { feeAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const FeeForm = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();

  // Prevent scroll wheel from changing number input values
  const handleNumberInputWheel = (e) => {
    e.currentTarget.blur();
  };
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    amount: '',
    type: 'delivery',
    is_active: true,
    description: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) loadFee();
  }, [id]);

  const loadFee = async () => {
    try {
      const response = await feeAPI.getOne(id);
      const data = response.data.data || response.data;
      setFormData({
        name: data.name || '',
        name_ar: data.name_ar || '',
        amount: data.amount || '',
        type: data.type || 'delivery',
        is_active: data.is_active ?? true,
        description: data.description || '',
      });
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (id) {
        await feeAPI.update(id, formData);
      } else {
        await feeAPI.create(formData);
      }
      navigate('/fees');
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{id ? t('fees.editFeeTitle') : t('fees.addFeeTitle')}</h2>
        <p className="text-gray-600 mt-1">{t('fees.feeDetails')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fees.nameEnglish')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Standard Delivery"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fees.nameArabic')}</label>
            <input
              type="text"
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., توصيل قياسي"
              dir="rtl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('fees.amountLabel')}</label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">{t('common.currency')}</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  onWheel={handleNumberInputWheel}
                  required
                  className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('fees.typeLabel')}</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="delivery">{t('fees.delivery')}</option>
                <option value="service">{t('fees.service')}</option>
                <option value="packaging">{t('fees.packaging')}</option>
                <option value="other">{t('fees.other')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('fees.descriptionLabel')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('fees.optionalDescription')}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              {t('fees.activeLabel')}
            </label>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.loading') : id ? t('fees.updateButton') : t('fees.createButton')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/fees')}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeeForm;
